import { useState, useRef } from "react";
import "@/App.css";
import { motion, useInView } from "framer-motion";
import { 
  Settings, 
  Cpu, 
  Microscope, 
  FileText, 
  Plus, 
  Trash2, 
  Upload, 
  Menu, 
  X, 
  ChevronDown,
  ChevronRight,
  Shield,
  Zap,
  Award,
  Lock,
  Package,
  Cog,
  FlaskConical,
  Ruler,
  Paintbrush,
  Home,
  Wrench,
  Building,
  ArrowRight,
  ArrowLeft,
  Check,
  ExternalLink,
  MessageSquare
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Checkbox } from "./components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Button } from "./components/ui/button";
import ENDAModal from "./components/ENDAModal";

// Main Tab Navigation Component
const TabNavigation = ({ activeTab, setActiveTab }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);

  const mainTabs = [
    { id: "home", label: "Home", icon: Home },
    { id: "toolkit", label: "Toolkit", icon: Wrench },
    { id: "build-now", label: "Build Now", icon: Package },
  ];

  const aboutOptions = [
    { id: "mechximize", label: "Mechximize", action: () => window.open("https://www.mechximize.in", "_blank") },
    { id: "betic", label: "BETIC", action: () => window.open("https://www.betic.org", "_blank") },
  ];

  return (
    <>
      <nav
        data-testid="main-navigation"
        className="fixed top-0 left-0 right-0 z-50 nav-glass"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button
              onClick={() => setActiveTab("home")}
              className="font-heading font-bold text-lg md:text-xl tracking-tight text-white"
              data-testid="logo-button"
            >
              MAKE<span className="text-amber-500">@</span>IITB
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-wider transition-all duration-200 border ${
                    activeTab === tab.id
                      ? "border-amber-500 bg-amber-500/10 text-amber-500"
                      : "border-transparent text-gray-400 hover:text-white hover:border-gray-700"
                  }`}
                  data-testid={`nav-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
              
              {/* About Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                  onBlur={() => setTimeout(() => setAboutDropdownOpen(false), 150)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-wider transition-all duration-200 border ${
                    ["initiative", "mechximize-about"].includes(activeTab)
                      ? "border-amber-500 bg-amber-500/10 text-amber-500"
                      : "border-transparent text-gray-400 hover:text-white hover:border-gray-700"
                  }`}
                  data-testid="nav-about"
                >
                  <Building className="w-4 h-4" />
                  About
                  <ChevronDown className={`w-3 h-3 transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {aboutDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 py-2 bg-[#0A0A0A] border border-[#262626] shadow-xl">
                    {aboutOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={option.action}
                        className="flex items-center justify-between w-full text-left px-4 py-2 text-sm font-mono text-gray-400 hover:text-white hover:bg-white/5"
                        data-testid={`nav-about-${option.id}`}
                      >
                        {option.label}
                        {option.id === "betic" && <ExternalLink className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-white p-2"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu md:hidden" data-testid="mobile-menu">
          <div className="flex justify-end p-4">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="text-white p-2"
              data-testid="mobile-menu-close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex flex-col items-center justify-center gap-6 h-[calc(100vh-80px)]">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMobileMenuOpen(false);
                }}
                className={`text-xl font-mono uppercase tracking-wider flex items-center gap-3 ${
                  activeTab === tab.id ? "text-amber-500" : "text-gray-400"
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
            <div className="border-t border-[#262626] pt-6 mt-2 w-48">
              <p className="text-gray-600 text-xs font-mono uppercase text-center mb-4">About</p>
              {aboutOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    option.action();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center gap-2 w-full py-2 text-gray-400 hover:text-white"
                >
                  {option.label}
                  {option.id === "betic" && <ExternalLink className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Home Tab Content
const HomeTab = ({ setActiveTab }) => {
  const handshakeRef = useRef(null);
  const isInView = useInView(handshakeRef, { once: false, amount: 0.5 });
  
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="min-h-[60vh] relative noise-overlay" data-testid="hero-section">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-20 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white mb-6">
              MAKE<span className="text-amber-500">@</span>IITB
            </h1>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto font-body">
              A Mechximize x BETIC Initiative
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="dirty-glass p-6 md:p-10 text-center mb-10 max-w-4xl mx-auto"
            data-testid="vision-statement"
          >
            <p className="font-accent text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">
              Our Vision
            </p>
            <h2 className="font-heading text-lg md:text-xl font-semibold text-white leading-relaxed">
              Empowering student teams, early-stage ventures, and research labs by providing 
              <span className="text-amber-500"> enterprise-grade supply chain access</span> for 
              <span className="text-cyan-500"> high-precision, rapid-turnaround manufacturing</span>.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Split View Section - Mechximize Left, BETIC Right */}
      <section className="relative" data-testid="split-view-section">
        <div className="split-screen">
          {/* Mechximize Side - LEFT */}
          <div className="mechximize-glow bg-[#050505] p-6 md:p-10 lg:p-14 flex flex-col justify-center border-r border-[#171717]">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-3">
                <Cog className="w-7 h-7 text-amber-500" />
                <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">
                  MECHXIMIZE
                </h2>
              </div>
              <p className="text-amber-500/80 text-xs font-mono uppercase tracking-wider mb-5">
                Enterprise-Grade Supply Chain
              </p>

              <div className="grid grid-cols-1 gap-2">
                {[
                  { 
                    icon: Zap, 
                    title: "On-Demand Capacity", 
                    desc: "Leverage a robust network of 30+ verified vendors and 20+ advanced machines. At your disposal: 10+ machining processes, 5+ advanced inspection services, and 10+ post-processing techniques for any material requirement."
                  },
                  { 
                    icon: Package, 
                    title: "Amazon-like Experience", 
                    desc: "Experience seamless manufacturing with end-to-end order tracking, flexible lead times, and component-level traceability—all backed by comprehensive inspection reports and material certificates."
                  },
                  { 
                    icon: Award, 
                    title: "Quality-First Precision", 
                    desc: "Our ISO 9001 certified quality management system delivers machining services across 50+ materials, achieving extreme precision with tolerances as tight as 5 microns."
                  },
                  { 
                    icon: Shield, 
                    title: "Proven Industry Expertise", 
                    desc: "Trusted by 25+ industry leaders—including Ideaforge, Manastu Space, and Inspecity—we have delivered 80+ unique component types across the Space Tech, Aerospace, Defence, Medical Device, and Robotics sectors."
                  },
                ].map((pillar, index) => (
                  <motion.div
                    key={pillar.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="pillar-card p-3 flex items-start gap-3"
                  >
                    <pillar.icon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-mono text-xs font-semibold text-white uppercase tracking-wide">
                        {pillar.title}
                      </h3>
                      <p className="text-gray-500 text-[11px] mt-1 leading-relaxed">{pillar.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* BETIC Side - RIGHT */}
          <div className="betic-glow bg-[#050505] p-6 md:p-10 lg:p-14 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-3">
                <FlaskConical className="w-7 h-7 text-cyan-500" />
                <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">
                  BETIC
                </h2>
              </div>
              <p className="text-cyan-500/80 text-xs font-mono uppercase tracking-wider mb-5">
                Excellence in Product Innovation
              </p>

              <div className="grid grid-cols-1 gap-2">
                {[
                  { 
                    icon: Award, 
                    title: "Certified Prototyping Infrastructure", 
                    desc: "An ISO 9001 and ISO 13485 certified facility dedicated to the rapid prototyping of complex parts in plastics, metals, ceramics, and electronics."
                  },
                  { 
                    icon: Cpu, 
                    title: "Advanced Product Engineering", 
                    desc: "Comprehensive services in design, simulation, and optimization. We refine components to achieve peak functionality and quality while minimizing production costs."
                  },
                  { 
                    icon: Microscope, 
                    title: "World-Class Medical R&D", 
                    desc: "A powerhouse of innovation that has identified 450 unmet medical needs, resulting in 240 novel concepts, 55 patent filings, and the successful development of 25 indigenous medical devices."
                  },
                  { 
                    icon: Shield, 
                    title: "Regulatory & Ecosystem Support", 
                    desc: "Driving social impact by connecting government, academia, and industry. To date, we have incubated 16 startups and licensed 14 products to local industry partners, fostering high-value job creation and affordable healthcare solutions."
                  },
                ].map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="pillar-card p-3 flex items-start gap-3 hover:border-cyan-500"
                  >
                    <feature.icon className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-mono text-xs font-semibold text-white uppercase tracking-wide">
                        {feature.title}
                      </h3>
                      <p className="text-gray-500 text-[11px] mt-1 leading-relaxed">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Handshake Animation Section */}
        <div 
          ref={handshakeRef}
          className="handshake-container bg-[#050505] relative overflow-hidden" 
          data-testid="handshake-section"
        >
          {/* Left Hand (Mechximize) */}
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : { x: -300, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute left-[5%] md:left-[15%] top-1/2 -translate-y-1/2"
          >
            <svg width="160" height="160" viewBox="0 0 200 200" className="text-amber-500">
              <g fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M40 100 L80 100 L90 80 L100 100 L110 70 L120 100 L130 75 L140 100 L150 85 L160 100 L180 100 L180 140 L160 160 L40 160 Z" />
                <path d="M40 100 L0 100 M40 160 L0 160" />
                <rect x="0" y="100" width="40" height="60" fill="currentColor" fillOpacity="0.15" />
                <line x1="80" y1="120" x2="160" y2="120" />
                <line x1="80" y1="135" x2="160" y2="135" />
              </g>
            </svg>
          </motion.div>

          {/* Right Hand (BETIC) - Inverted */}
          <motion.div
            initial={{ x: 300, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : { x: 300, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute right-[5%] md:right-[15%] top-1/2 -translate-y-1/2 rotate-180"
          >
            <svg width="160" height="160" viewBox="0 0 200 200" className="text-cyan-500">
              <g fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M40 100 L80 100 L90 80 L100 100 L110 70 L120 100 L130 75 L140 100 L150 85 L160 100 L180 100 L180 140 L160 160 L40 160 Z" />
                <path d="M40 100 L0 100 M40 160 L0 160" />
                <rect x="0" y="100" width="40" height="60" fill="currentColor" fillOpacity="0.15" />
                <line x1="80" y1="120" x2="160" y2="120" />
                <line x1="80" y1="135" x2="160" y2="135" />
              </g>
            </svg>
          </motion.div>

          {/* Center Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-center px-4">
              <span className="fusion-text">What's in it for you?</span>
            </h2>
          </motion.div>
        </div>

        {/* What's In It For You - Full Content Section */}
        <div className="bg-[#050505] py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            {/* Byline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <p className="font-heading text-lg md:text-xl text-amber-500 font-semibold mb-6">
                Resilient Product Development Needs a Resilient Supply Chain.
              </p>
              <p className="text-gray-400 leading-relaxed max-w-3xl mx-auto">
                Stop wasting your time, money, and energy chasing unreliable external vendors who deliver poor-quality parts and experience constant delays, all for high prices. Through the <span className="text-white font-semibold">MAKE @ IITB</span> initiative, we bridge the gap between your research lab and enterprise-grade production.
              </p>
            </motion.div>

            {/* 4 Key Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {[
                {
                  num: "01",
                  title: "Enterprise-Grade Quality at Your Doorstep",
                  desc: "Access high-precision manufacturing that meets industry standards without leaving the IITB ecosystem.",
                  color: "amber"
                },
                {
                  num: "02",
                  title: "No Minimum Order Quantity (MOQ)",
                  desc: "We believe in the power of iteration; whether you need a single prototype (1 pc) or a full batch, you get the same enterprise-grade precision without the burden of bulk requirements.",
                  color: "cyan"
                },
                {
                  num: "03",
                  title: "Rapid Turnaround for Rapid Iteration",
                  desc: "Eliminate the \"vendor-chasing\" cycle with optimized lead times tailored to the fast-paced needs of early-stage ventures and student teams.",
                  color: "green"
                },
                {
                  num: "04",
                  title: "Absolute IP Security",
                  desc: "Your designs and research stay protected within a secure ISO 27001-certified infrastructure, ensuring your intellectual property is never compromised during the manufacturing process.",
                  color: "purple"
                }
              ].map((benefit, index) => (
                <motion.div
                  key={benefit.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="border border-[#262626] bg-[#0A0A0A] p-6 hover:border-gray-600 transition-colors"
                  data-testid={`benefit-${index}`}
                >
                  <span className={`font-mono text-xs text-${benefit.color}-500 mb-2 block`}>{benefit.num}</span>
                  <h3 className="font-heading text-lg font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{benefit.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* The Bottom Line */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="dirty-glass p-6 md:p-8 text-center mb-12"
            >
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">The Bottom Line</p>
              <p className="text-white text-lg md:text-xl font-medium">
                We take the <span className="text-amber-500">"noise"</span> out of manufacturing so you can focus on what matters most: <span className="fusion-text font-semibold">Engineering the future.</span>
              </p>
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="text-gray-400 mb-6">
                Access our comprehensive toolkit or start your manufacturing order today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setActiveTab("toolkit")}
                  className="btn-mechanical bg-transparent border border-white text-white hover:bg-white hover:text-black font-mono uppercase tracking-wider text-sm rounded-none px-8 py-4"
                  data-testid="explore-toolkit-btn"
                >
                  Explore Toolkit
                </Button>
                <Button
                  onClick={() => setActiveTab("build-now")}
                  className="btn-mechanical pulse-glow bg-amber-500 text-black hover:bg-amber-400 font-mono uppercase tracking-wider text-sm rounded-none px-8 py-4"
                  data-testid="start-building-btn"
                >
                  Start Building <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Initiative Page
const InitiativePage = () => {
  return (
    <div className="pt-24 pb-20 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-6 text-center">
            THE INITIATIVE
          </h1>
          <div className="dirty-glass p-8 md:p-12 mb-8">
            <p className="text-gray-300 leading-relaxed mb-6">
              <span className="text-amber-500 font-semibold">MAKE @ IITB</span> is a groundbreaking collaboration between Mechximize and BETIC, designed to bridge the gap between innovative ideas and manufacturing reality.
            </p>
            <p className="text-gray-400 leading-relaxed mb-6">
              Our mission is to democratize access to enterprise-grade manufacturing capabilities for the IIT Bombay ecosystem - including early-stage ventures, D-Labs, research groups, and student teams.
            </p>
            <p className="text-gray-400 leading-relaxed">
              By combining Mechximize's manufacturing supply chain expertise with BETIC's biomedical innovation infrastructure, we're creating a one-stop platform for turning concepts into reality.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-[#262626] p-6 bg-[#0A0A0A]">
              <h3 className="font-mono text-amber-500 text-sm uppercase tracking-wider mb-3">What We Offer</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-500 mt-0.5" /> On-demand manufacturing capacity</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-500 mt-0.5" /> Quality-first approach</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-500 mt-0.5" /> IP protection</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-500 mt-0.5" /> Streamlined ordering</li>
              </ul>
            </div>
            <div className="border border-[#262626] p-6 bg-[#0A0A0A]">
              <h3 className="font-mono text-cyan-500 text-sm uppercase tracking-wider mb-3">Who We Serve</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-500 mt-0.5" /> Early-stage ventures</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-500 mt-0.5" /> D-Labs & Research Groups</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-500 mt-0.5" /> Student Teams</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-500 mt-0.5" /> Healthcare Innovators</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Mechximize About Page
const MechximizeAboutPage = () => {
  return (
    <div className="pt-24 pb-20 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <Cog className="w-12 h-12 text-amber-500" />
            <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white">
              MECHXIMIZE
            </h1>
          </div>
          
          <div className="dirty-glass p-8 md:p-12 mb-8">
            <p className="text-gray-300 leading-relaxed mb-6">
              Mechximize is an enterprise-grade manufacturing supply chain platform designed specifically for the innovation ecosystem.
            </p>
            <p className="text-gray-400 leading-relaxed">
              We connect innovators with a curated network of manufacturing partners, ensuring quality, speed, and IP security at every step of the production process.
            </p>
          </div>
          
          <h3 className="font-mono text-amber-500 text-sm uppercase tracking-wider mb-4">Our Four Pillars</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: Zap, title: "On-Demand Capacity", desc: "Access manufacturing resources when you need them, without long-term commitments or minimum orders." },
              { icon: Package, title: "Amazon-like Experience", desc: "Simple ordering, transparent tracking, and reliable delivery - manufacturing made easy." },
              { icon: Award, title: "Quality First", desc: "Rigorous quality control standards ensure your parts meet specifications every time." },
              { icon: Lock, title: "IP Security", desc: "Your designs and intellectual property are protected through secure processes and NDAs." },
            ].map((pillar) => (
              <div key={pillar.title} className="border border-[#262626] p-5 bg-[#0A0A0A]">
                <pillar.icon className="w-6 h-6 text-amber-500 mb-3" />
                <h4 className="font-mono text-white text-sm uppercase tracking-wider mb-2">{pillar.title}</h4>
                <p className="text-gray-500 text-sm">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Toolkit Tab Content
const ToolkitTab = () => {
  const [materialSubTab, setMaterialSubTab] = useState("primary");
  
  const machiningData = [
    { category: "CNC Turning", physics: "Rotational Cutting", uses: "Cylindrical Parts, Shafts", limitations: "Limited to rotational geometry", machines: "Mazak QT-250, Haas ST-10" },
    { category: "CNC Milling", physics: "Multi-axis Cutting", uses: "Complex 3D Geometries", limitations: "Tool access constraints", machines: "DMG Mori DMU 50, Haas VF-2" },
    { category: "Wire EDM", physics: "Electrical Discharge", uses: "Intricate Profiles, Hard Materials", limitations: "Slow process, conductive materials only", machines: "Fanuc Robocut, Sodick VL400Q" },
    { category: "Grinding", physics: "Abrasive Machining", uses: "Surface Finishing, Tight Tolerances", limitations: "Material removal rate", machines: "Studer S33, Kellenberger" },
    { category: "Laser Cutting", physics: "Thermal Ablation", uses: "Sheet Metal, 2D Profiles", limitations: "Thickness limitations", machines: "Trumpf TruLaser 3030" },
  ];

  // Table 1: Primary Metals & Alloys
  const primaryMetalsData = [
    { category: "Aluminum", grade: "Al 6061", properties: "Good weldability, corrosion resistant", applications: "Structural frames, marine", datasheet: "ASTM B209" },
    { category: "Aluminum", grade: "Al 6061-T6", properties: "Heat treated, high strength", applications: "Aerospace, automotive", datasheet: "ASTM B209" },
    { category: "Aluminum", grade: "Al 2024", properties: "High fatigue resistance", applications: "Aircraft structures", datasheet: "AMS 4037" },
    { category: "Aluminum", grade: "Al 5052", properties: "Excellent corrosion resistance", applications: "Marine, fuel tanks", datasheet: "ASTM B209" },
    { category: "Aluminum", grade: "Al 5083", properties: "High strength, weldable", applications: "Shipbuilding, cryogenic", datasheet: "ASTM B209" },
    { category: "Aluminum", grade: "Al 7075", properties: "Very high strength", applications: "Aerospace, tooling", datasheet: "AMS 4045" },
    { category: "Aluminum", grade: "Al 7075-T6", properties: "Peak aged, highest strength", applications: "Aircraft, military", datasheet: "AMS 4045" },
    { category: "Aluminum", grade: "Al 7175", properties: "Improved toughness", applications: "Aircraft forgings", datasheet: "AMS 4179" },
    { category: "Steel & Die Steel", grade: "1018", properties: "Low carbon, good machinability", applications: "General purpose, shafts", datasheet: "ASTM A108" },
    { category: "Steel & Die Steel", grade: "1045", properties: "Medium carbon, good strength", applications: "Gears, shafts, bolts", datasheet: "ASTM A29" },
    { category: "Steel & Die Steel", grade: "4130", properties: "Chromoly, heat treatable", applications: "Aircraft tubing, automotive", datasheet: "AMS 6350" },
    { category: "Steel & Die Steel", grade: "4140", properties: "High strength, wear resistant", applications: "Gears, crankshafts", datasheet: "ASTM A29" },
    { category: "Steel & Die Steel", grade: "4340", properties: "Deep hardening, tough", applications: "Landing gear, heavy duty", datasheet: "AMS 6414" },
    { category: "Steel & Die Steel", grade: "M-2 (HSS)", properties: "High speed steel, wear resistant", applications: "Cutting tools, drills", datasheet: "ASTM A600" },
    { category: "Steel & Die Steel", grade: "D-2 (H.C.H.CR.)", properties: "High carbon high chromium", applications: "Blanking dies, gauges", datasheet: "ASTM A681" },
    { category: "Steel & Die Steel", grade: "H-13", properties: "Hot work, thermal shock resistant", applications: "Die casting, extrusion", datasheet: "ASTM A681" },
    { category: "Steel & Die Steel", grade: "En31", properties: "High carbon chromium", applications: "Bearings, rollers", datasheet: "IS 4398" },
    { category: "Stainless Steel", grade: "SS303", properties: "Free machining, good corrosion", applications: "Screws, fittings", datasheet: "ASTM A582" },
    { category: "Stainless Steel", grade: "SS304", properties: "18-8 austenitic, versatile", applications: "Food equipment, medical", datasheet: "ASTM A240" },
    { category: "Stainless Steel", grade: "SS316", properties: "Molybdenum added, marine grade", applications: "Chemical, marine", datasheet: "ASTM A240" },
    { category: "Stainless Steel", grade: "SS316L", properties: "Low carbon, weldable", applications: "Implants, pharma", datasheet: "ASTM A240" },
    { category: "Stainless Steel", grade: "SS420", properties: "Martensitic, hardenable", applications: "Surgical, cutlery", datasheet: "ASTM A276" },
    { category: "Stainless Steel", grade: "17-7ph", properties: "Precipitation hardening", applications: "Springs, aerospace", datasheet: "AMS 5528" },
    { category: "Copper & Minor Metals", grade: "C101 (T2)", properties: "High conductivity copper", applications: "Electrical, heat sinks", datasheet: "ASTM B152" },
    { category: "Copper & Minor Metals", grade: "C110 (TU0)", properties: "Oxygen-free copper", applications: "Electronics, vacuum", datasheet: "ASTM B152" },
    { category: "Copper & Minor Metals", grade: "Brass", properties: "Cu-Zn alloy, machinable", applications: "Fittings, decorative", datasheet: "ASTM B16" },
    { category: "Copper & Minor Metals", grade: "Tin", properties: "Low melting, corrosion resistant", applications: "Soldering, coating", datasheet: "ASTM B339" },
    { category: "Copper & Minor Metals", grade: "Zinc", properties: "Corrosion protection", applications: "Die casting, galvanizing", datasheet: "ASTM B86" },
  ];

  // Table 2: High-Performance & Special Alloys
  const highPerfAlloysData = [
    { category: "Superalloys", grade: "Inconel 600", properties: "Oxidation resistant, high temp", applications: "Furnace, heat treating", datasheet: "AMS 5540" },
    { category: "Superalloys", grade: "Inconel 718", properties: "High strength at temp, weldable", applications: "Turbines, aerospace", datasheet: "AMS 5662" },
    { category: "Superalloys", grade: "Inconel X-750", properties: "Precipitation hardened", applications: "Gas turbines, springs", datasheet: "AMS 5542" },
    { category: "Superalloys", grade: "HAYNES 25", properties: "Cobalt-based, high temp", applications: "Combustion chambers", datasheet: "AMS 5796" },
    { category: "Superalloys", grade: "Nickel 200", properties: "Pure nickel, corrosion resistant", applications: "Chemical processing", datasheet: "ASTM B160" },
    { category: "Superalloys", grade: "Monel 400", properties: "Ni-Cu, seawater resistant", applications: "Marine, valves", datasheet: "AMS 4544" },
    { category: "Superalloys", grade: "Monel K500", properties: "Age hardenable Monel", applications: "Pump shafts, fasteners", datasheet: "AMS 4676" },
    { category: "Superalloys", grade: "Waspaloy", properties: "Ni-based, creep resistant", applications: "Turbine discs, rings", datasheet: "AMS 5544" },
    { category: "Superalloys", grade: "Hastelloy X", properties: "Oxidation resistant", applications: "Gas turbine combustors", datasheet: "AMS 5536" },
    { category: "Superalloys", grade: "Haynes 188", properties: "Cobalt, oxidation resistant", applications: "Afterburner components", datasheet: "AMS 5608" },
    { category: "Titanium", grade: "TA1 (Gr1)", properties: "Commercially pure, ductile", applications: "Chemical processing", datasheet: "ASTM B265" },
    { category: "Titanium", grade: "TA2 (Gr2)", properties: "Good strength, weldable", applications: "Marine, desalination", datasheet: "ASTM B265" },
    { category: "Titanium", grade: "TC4 (Ti-6Al-4V)", properties: "Alpha-beta, high strength", applications: "Aerospace, implants", datasheet: "AMS 4911" },
    { category: "Titanium", grade: "Ti Grade 5", properties: "Most common Ti alloy", applications: "Aerospace, medical", datasheet: "ASTM F136" },
    { category: "Titanium", grade: "Ti-6-2-4-6", properties: "High strength, deep hardening", applications: "Compressor blades", datasheet: "AMS 4981" },
    { category: "Refractory & Industrial", grade: "Molybdenum", properties: "High melting point, strong", applications: "Furnace, electrodes", datasheet: "ASTM B386" },
    { category: "Refractory & Industrial", grade: "Tungsten", properties: "Highest melting point metal", applications: "Electrodes, ballast", datasheet: "ASTM B760" },
    { category: "Refractory & Industrial", grade: "Tantalum", properties: "Highly corrosion resistant", applications: "Chemical equipment", datasheet: "ASTM B708" },
    { category: "Refractory & Industrial", grade: "Zirconium Zr702", properties: "Nuclear grade, corrosion resistant", applications: "Nuclear, chemical", datasheet: "ASTM B550" },
    { category: "Ferro Alloys", grade: "Ferro Moly", properties: "Mo addition for steel", applications: "Alloying agent", datasheet: "ASTM A132" },
    { category: "Ferro Alloys", grade: "Ferro Chrome (LC)", properties: "Low carbon Cr addition", applications: "Stainless steel production", datasheet: "IS 1473" },
    { category: "Ferro Alloys", grade: "Ferro Manganese", properties: "Mn addition, deoxidizer", applications: "Steel production", datasheet: "ASTM A99" },
    { category: "Ferro Alloys", grade: "Ferro Nickel", properties: "Ni addition for steel", applications: "Stainless, special steels", datasheet: "GB/T 7737" },
  ];

  // Table 3: Special Materials & Engineering Plastics
  const specialMaterialsData = [
    { category: "Eng. Plastics", grade: "PMMA (Clear)", properties: "Optical clarity, UV stable", applications: "Displays, light guides", datasheet: "ASTM D788" },
    { category: "Eng. Plastics", grade: "PMMA (Colors)", properties: "Colored acrylic, machinable", applications: "Signage, decorative", datasheet: "ASTM D788" },
    { category: "Eng. Plastics", grade: "PA/Nylon 6", properties: "Good wear, self-lubricating", applications: "Gears, bearings", datasheet: "ASTM D4066" },
    { category: "Eng. Plastics", grade: "PA/Nylon + GF", properties: "Glass reinforced, high strength", applications: "Structural parts", datasheet: "ASTM D4066" },
    { category: "Eng. Plastics", grade: "PEEK", properties: "High temp, chemical resistant", applications: "Implants, aerospace", datasheet: "ASTM D6262" },
    { category: "Eng. Plastics", grade: "Delrin (POM)", properties: "Low friction, dimensional stability", applications: "Gears, fasteners", datasheet: "ASTM D4181" },
    { category: "Eng. Plastics", grade: "UHMWPE", properties: "Ultra high MW, wear resistant", applications: "Joint replacements", datasheet: "ASTM D4020" },
    { category: "Special Elements", grade: "Carbon", properties: "Lightweight, conductive", applications: "Electrodes, brushes", datasheet: "ASTM C709" },
    { category: "Special Elements", grade: "Carbide (WC)", properties: "Extremely hard, wear resistant", applications: "Cutting tools, dies", datasheet: "ISO 513" },
    { category: "Special Elements", grade: "Graphite", properties: "Self-lubricating, thermal", applications: "EDM electrodes, crucibles", datasheet: "ASTM C709" },
    { category: "Special Elements", grade: "Silicon", properties: "Semiconductor, high purity", applications: "Electronics, solar", datasheet: "SEMI M1" },
    { category: "Special Elements", grade: "Cobalt", properties: "Hard, magnetic, corrosion resistant", applications: "Cutting tools, magnets", datasheet: "ASTM B170" },
    { category: "Specialized Iron", grade: "Invar", properties: "Very low thermal expansion", applications: "Precision instruments", datasheet: "ASTM F1684" },
    { category: "Specialized Iron", grade: "Core Iron", properties: "High magnetic permeability", applications: "Transformers, motors", datasheet: "ASTM A848" },
    { category: "Specialized Iron", grade: "Vimvar Rods", properties: "Vacuum melted, high purity", applications: "Aerospace, nuclear", datasheet: "AMS 2300" },
  ];

  const postProcessingData = [
    // Electro/Chemical Class
    { category: "Electro/Chemical", process: "Anodizing", physics: "Artificial formation and coloring of a hard, porous chemical oxide film on aluminum alloys.", types: "Sulfuric Acid, Oxalic Acid, Hard Anodizing, Porcelain Anodizing", material: "Aluminum, Magnesium, Titanium Alloys", applicability: "Requires electrical conductivity; suitable for complex geometries via immersion", specifications: "Standard: 5-25μm; Hardcoat: Up to 100μm", applications: "Aerospace, Consumer electronics, Medical instruments" },
    { category: "Electro/Chemical", process: "Electroplating", physics: "Electrolytic deposition of a metallic coating through an electrochemical reaction in a salt solution.", types: "Chrome, Gold, Nickel, Zinc, Silver, Copper", material: "Conductive metals and specific engineering plastics", applicability: "Requires electrical contact; immersion-based for uniform external coverage", specifications: "Precision Thickness: 0.5-50μm", applications: "Corrosion resistance, electrical conductivity, aesthetics" },
    { category: "Electro/Chemical", process: "Electroless Plating", physics: "Autocatalytic chemical reduction that forms a non-crystalline coating without an external electric field.", types: "Electroless Nickel, Electroless Copper", material: "Steel, Aluminum, Plastics (ABS, PC)", applicability: "Provides uniform thickness on complex internal geometries, threads, and deep recesses", specifications: "Thickness: 1-100μm; Highly uniform", applications: "Automotive parts, aerospace components, EMI shielding" },
    { category: "Electro/Chemical", process: "Chemical/Electrochemical Polishing", physics: "Chemical or electrochemical dissolution of surface peaks to create a microscopic leveling effect.", types: "Chemical Polishing, Electropolishing", material: "Stainless Steel, Aluminum, Titanium", applicability: "Complex geometries where mechanical polishing cannot reach", specifications: "Reduces Ra roughness by up to 50%; removes micro-burrs", applications: "Medical instruments, semiconductor piping, food processing" },
    { category: "Electro/Chemical", process: "Electrophoresis (E-Coat)", physics: "Migration of charged particles in a fluid under an electric field to deposit a continuous film.", types: "Cathodic Epoxy, Anodic Acrylic", material: "Conductive metals", applicability: "Excellent for complex parts requiring high corrosion resistance; highly uniform", specifications: "Film Thickness: 10-40μm; Curing: 180°C", applications: "Automotive bodies, heavy machinery, hardware" },
    { category: "Electro/Chemical", process: "Physical Vapor Deposition (PVD)", physics: "Vacuum deposition where solid material is vaporized and deposited as a thin film.", types: "Sputtering, Evaporation", material: "Titanium, Stainless Steel, Ceramics, Plastics", applicability: "Vacuum-compatible parts; line-of-sight process", specifications: "Coating: 0.5-5μm; Extreme hardness, low friction", applications: "Cutting tools, high-end consumer goods, watches" },
    { category: "Electro/Chemical", process: "Chemical Vapor Deposition (CVD)", physics: "Chemical reaction of gaseous precursors on a heated substrate to produce solid high-performance films.", types: "Thermal CVD, Plasma-Enhanced CVD", material: "Carbides, Nitrides, Silicides", applicability: "Requires high temperatures; non-line-of-sight", specifications: "High-purity coatings; excellent step coverage", applications: "Semiconductors, protective coatings on tools" },
    // Coating Class
    { category: "Coating", process: "Spraying", physics: "Atomized liquid coating applied via air pressure to the part's surface.", types: "Air Spray, Airless Spray", material: "Metals, Plastics, Wood", applicability: "General protective and decorative coating for all sizes", specifications: "Variable thickness; Endless color matching", applications: "Consumer electronics, enclosures, automotive" },
    { category: "Coating", process: "Powder Coating", physics: "Electrostatic application of dry powder cured under high heat to form a durable skin.", types: "Thermoset, Thermoplastic", material: "Steels, Aluminum, Zinc", applicability: "Parts must withstand high curing temperatures", specifications: "Thickness: 60-120μm; High durability", applications: "Industrial frames, outdoor machinery" },
    { category: "Coating", process: "Painting", physics: "Application of a pigment vehicle mixture to protect and color the surface.", types: "Enamel, Polyurethane, Acrylic", material: "Metals, Plastics, Composites", applicability: "Universal applicability, often used with primers", specifications: "Custom color and gloss levels (matte to high-gloss)", applications: "Aesthetics, weather resistance" },
    // Machining Class
    { category: "Machining", process: "Sandblasting", physics: "High-speed projection of abrasive media using compressed air.", types: "Manual Blasting, Automated Tumbling", material: "Metals, Ceramics, High-density Plastics", applicability: "Line-of-sight process; external surfaces only", specifications: "Ra Surface Roughness: 0.8-6.3μm", applications: "Surface preparation, Deburring, Matte finishing" },
    { category: "Machining", process: "Brushing", physics: "Mechanical rubbing using abrasive belts or wheels to create directional lines.", types: "Hairline finish, Satin finish", material: "Aluminum, Stainless Steel", applicability: "Flat surfaces or continuous cylindrical surfaces", specifications: "Removes defects while adding decorative texture", applications: "Kitchen appliances, elevator panels, architectural hardware" },
    { category: "Machining", process: "Rolling", physics: "Parts are tumbled with abrasive media in a rotating or vibratory drum.", types: "Vibratory Finishing, Barrel Tumbling", material: "Small to medium-sized metal parts", applicability: "Bulk processing; edge rounding and surface smoothing", specifications: "Reduces sharp edges without precise dimensional changes", applications: "Mass deburring of stamped or machined parts" },
    { category: "Machining", process: "Polishing", physics: "Mechanical surface smoothing using progressively finer abrasives and buffing compounds.", types: "Rough Polishing, Mirror Polishing", material: "Most solid metals and hard plastics", applicability: "External surfaces; labor-intensive for complex geometries", specifications: "Can achieve Ra < 0.1μm (Mirror finish)", applications: "Optical reflectors, decorative trim, medical implants" },
    { category: "Machining", process: "Machining", physics: "Material removal via cutting tools to achieve final dimensions and surface texture.", types: "Milling, Turning, Grinding", material: "Metals, Plastics, Composites", applicability: "Primary shaping process that doubles as a finishing process", specifications: "Tolerances down to 5 microns", applications: "Precision components, engine blocks" },
  ];

  const inspectionData = [
    { process: "Optical Surface Profiling", instrument: "Wyko NT9100 Optical Profiling System", specs: "Sub-nanometer vertical resolution at all magnifications", suited: "MEMS, thick films, optics, ceramics, and advanced materials research", category: "Special" },
    { process: "Optical 3D Surface & Form Measurement", instrument: "Alicona InfiniteFocus G5", specs: "Vertical resolution of up to 10nm", suited: "Materials with varying reflection properties, areas, and volumes", category: "Special" },
    { process: "True Color 3D Optical Profiling", instrument: "Zeta-20 Optical Profiler", specs: "Film thickness: 30nm to 100µm; Defect inspection > 1µm", suited: "3D step height (nm to mm), roughness, waviness, and 2D thin film stress", category: "Special" },
    { process: "Surface Roughness & Contour Measurement", instrument: "Mitutoyo Formtracer Extreme CS-5000CNC / CS-H5000CNC", specs: "Range X-Axis: 200mm, Resolution X-Axis: 0.005µm", suited: "High-precision geometric profiling and alpha-axis tracking", category: "Special" },
    { process: "Coordinate Measuring", instrument: "Mitutoyo Crysta-Plus M443", specs: "High precision continuous scanning with 0.1µm resolution", suited: "Geometric measurement using motorized indexing probe heads", category: "Special" },
    { process: "Optical Coordinate Measurement", instrument: "Alicona µCMM", specs: "High-resolution optical 3D measurement", suited: "Micro-precision optical inspection of complex geometries", category: "Special" },
    { process: "Vision Measuring, 3D Scanning & 2D Contour", instrument: "Rapid-I Vision Measuring System (v2015J, v4020J)", specs: "Magnification up to 165X; Renishaw 3-Axis Touch Probe; Automated CNC with Auto-Focus", suited: "High-volume inspections: Automotive, Aerospace, Electronics, Medical, Cutting Tools", category: "Special" },
  ];

  const renderMaterialTable = (data, colorClass) => (
    <div className="overflow-x-auto">
      <table className="directory-table w-full text-left" data-testid="material-table">
        <thead>
          <tr>
            <th className="p-3 text-gray-400 text-xs">Category</th>
            <th className="p-3 text-gray-400 text-xs">Grade</th>
            <th className="p-3 text-gray-400 text-xs">Properties</th>
            <th className="p-3 text-gray-400 text-xs">Applications</th>
            <th className="p-3 text-gray-400 text-xs">Datasheet</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index} className="text-gray-300 hover:bg-[#0A0A0A] border-b border-[#171717]">
              <td className="p-3 text-sm text-gray-500">{row.category}</td>
              <td className={`p-3 font-mono text-sm ${colorClass}`}>{row.grade}</td>
              <td className="p-3 text-xs text-gray-400">{row.properties}</td>
              <td className="p-3 text-xs">{row.applications}</td>
              <td className="p-3 text-xs font-mono text-gray-600">{row.datasheet}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="pt-24 pb-20 min-h-screen bg-[#050505] noise-overlay">
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-12">
          <p className="font-accent text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">Resources</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white">TOOLKIT DIRECTORIES</h1>
        </motion.div>

        <Tabs defaultValue="machining" className="w-full">
          <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent mb-8" data-testid="toolkit-tabs">
            <TabsTrigger value="machining" className="tab-trigger px-3 md:px-5 py-3 text-xs data-[state=active]:bg-[#171717] data-[state=active]:border-amber-500 data-[state=active]:text-amber-500" data-testid="tab-machining">
              <Settings className="w-4 h-4 mr-1.5" />Machining
            </TabsTrigger>
            <TabsTrigger value="material" className="tab-trigger px-3 md:px-5 py-3 text-xs data-[state=active]:bg-[#171717] data-[state=active]:border-amber-500 data-[state=active]:text-amber-500" data-testid="tab-material">
              <Cpu className="w-4 h-4 mr-1.5" />Material
            </TabsTrigger>
            <TabsTrigger value="post-processing" className="tab-trigger px-3 md:px-5 py-3 text-xs data-[state=active]:bg-[#171717] data-[state=active]:border-amber-500 data-[state=active]:text-amber-500" data-testid="tab-post-processing">
              <Paintbrush className="w-4 h-4 mr-1.5" />Post Processing
            </TabsTrigger>
            <TabsTrigger value="inspection" className="tab-trigger px-3 md:px-5 py-3 text-xs data-[state=active]:bg-[#171717] data-[state=active]:border-amber-500 data-[state=active]:text-amber-500" data-testid="tab-inspection">
              <Ruler className="w-4 h-4 mr-1.5" />Inspection
            </TabsTrigger>
          </TabsList>

          <TabsContent value="machining" className="mt-0">
            <div className="overflow-x-auto">
              <table className="directory-table w-full text-left" data-testid="machining-table">
                <thead>
                  <tr>
                    <th className="p-4 text-gray-400">Process Category</th>
                    <th className="p-4 text-gray-400">Process Physics</th>
                    <th className="p-4 text-gray-400">Uses/Geometry</th>
                    <th className="p-4 text-gray-400">Limitations</th>
                    <th className="p-4 text-gray-400">Machines Available</th>
                  </tr>
                </thead>
                <tbody>
                  {machiningData.map((row, index) => (
                    <tr key={index} className="text-gray-300 hover:bg-[#0A0A0A]">
                      <td className="p-4 font-mono text-sm text-amber-500">{row.category}</td>
                      <td className="p-4 text-sm">{row.physics}</td>
                      <td className="p-4 text-sm">{row.uses}</td>
                      <td className="p-4 text-sm text-gray-500">{row.limitations}</td>
                      <td className="p-4 text-sm font-mono">{row.machines}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="material" className="mt-0">
            {/* Material Sub-tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              <button
                onClick={() => setMaterialSubTab("primary")}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border transition-all ${
                  materialSubTab === "primary" 
                    ? "border-cyan-500 bg-cyan-500/10 text-cyan-500" 
                    : "border-[#262626] text-gray-500 hover:text-white hover:border-gray-600"
                }`}
                data-testid="material-subtab-primary"
              >
                Primary Metals & Alloys
              </button>
              <button
                onClick={() => setMaterialSubTab("highperf")}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border transition-all ${
                  materialSubTab === "highperf" 
                    ? "border-purple-500 bg-purple-500/10 text-purple-500" 
                    : "border-[#262626] text-gray-500 hover:text-white hover:border-gray-600"
                }`}
                data-testid="material-subtab-highperf"
              >
                High-Performance Alloys
              </button>
              <button
                onClick={() => setMaterialSubTab("special")}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border transition-all ${
                  materialSubTab === "special" 
                    ? "border-green-500 bg-green-500/10 text-green-500" 
                    : "border-[#262626] text-gray-500 hover:text-white hover:border-gray-600"
                }`}
                data-testid="material-subtab-special"
              >
                Special Materials & Plastics
              </button>
            </div>

            {/* Table 1: Primary Metals */}
            {materialSubTab === "primary" && (
              <div>
                {renderMaterialTable(primaryMetalsData, "text-cyan-400")}
              </div>
            )}

            {/* Table 2: High-Performance Alloys */}
            {materialSubTab === "highperf" && (
              <div>
                {renderMaterialTable(highPerfAlloysData, "text-purple-400")}
              </div>
            )}

            {/* Table 3: Special Materials */}
            {materialSubTab === "special" && (
              <div>
                {renderMaterialTable(specialMaterialsData, "text-green-400")}
              </div>
            )}
          </TabsContent>

          <TabsContent value="post-processing" className="mt-0">
            <div className="overflow-x-auto">
              <table className="directory-table w-full text-left" data-testid="post-processing-table">
                <thead>
                  <tr>
                    <th className="p-3 text-gray-400 text-xs">Category</th>
                    <th className="p-3 text-gray-400 text-xs">Process</th>
                    <th className="p-3 text-gray-400 text-xs">Physics</th>
                    <th className="p-3 text-gray-400 text-xs">Types</th>
                    <th className="p-3 text-gray-400 text-xs">Material</th>
                    <th className="p-3 text-gray-400 text-xs">Applicability</th>
                    <th className="p-3 text-gray-400 text-xs">Specifications</th>
                    <th className="p-3 text-gray-400 text-xs">Applications</th>
                  </tr>
                </thead>
                <tbody>
                  {postProcessingData.map((row, index) => (
                    <tr key={index} className="text-gray-300 hover:bg-[#0A0A0A] border-b border-[#171717]">
                      <td className="p-3 text-xs">
                        <span className={`px-2 py-1 text-[10px] font-mono ${
                          row.category === 'Electro/Chemical' ? 'bg-purple-500/10 text-purple-400' : 
                          row.category === 'Coating' ? 'bg-amber-500/10 text-amber-400' : 
                          'bg-cyan-500/10 text-cyan-400'
                        }`}>
                          {row.category}
                        </span>
                      </td>
                      <td className="p-3 font-mono text-sm text-white font-semibold">{row.process}</td>
                      <td className="p-3 text-[11px] text-gray-400 max-w-[200px]">{row.physics}</td>
                      <td className="p-3 text-[11px]">{row.types}</td>
                      <td className="p-3 text-[11px]">{row.material}</td>
                      <td className="p-3 text-[11px] text-gray-500 max-w-[150px]">{row.applicability}</td>
                      <td className="p-3 text-[11px] font-mono text-gray-400">{row.specifications}</td>
                      <td className="p-3 text-[11px]">{row.applications}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="inspection" className="mt-0">
            <div className="overflow-x-auto">
              <table className="directory-table w-full text-left" data-testid="inspection-table">
                <thead>
                  <tr>
                    <th className="p-4 text-gray-400">Process</th>
                    <th className="p-4 text-gray-400">Instrument</th>
                    <th className="p-4 text-gray-400">Key Specifications</th>
                    <th className="p-4 text-gray-400">Best Suited For</th>
                    <th className="p-4 text-gray-400">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {inspectionData.map((row, index) => (
                    <tr key={index} className="text-gray-300 hover:bg-[#0A0A0A]">
                      <td className="p-4 font-mono text-sm text-green-500">{row.process}</td>
                      <td className="p-4 text-sm">{row.instrument}</td>
                      <td className="p-4 text-sm font-mono">{row.specs}</td>
                      <td className="p-4 text-sm">{row.suited}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-mono ${row.category === 'Standard' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>{row.category}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Build Now Tab Content - 2 Step Form
const BuildNowTab = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showENDAModal, setShowENDAModal] = useState(false);
  const [orderItems, setOrderItems] = useState([
    { 
      id: 1, 
      partName: "", 
      material: "", 
      quantity: 1, 
      stepFile: null, 
      drawingFile: null,
      instructions: "",
      instructionsPdf: null,
      postProcessing: { enabled: false, process: "" },
      inspection: { enabled: false, processes: [] }
    },
  ]);
  const [userDetails, setUserDetails] = useState({
    name: "",
    ldapId: "",
    teamName: "",
    teamCategory: "",
    labName: "",
    department: "",
    phone: "",
  });
  const [leadTime, setLeadTime] = useState("");

  // Materials from toolkit - Organized by Category
  const materialCategories = [
    {
      category: "Aluminum",
      grades: ["Al 6061", "Al 6061-T6", "Al 2024", "Al 5052", "Al 5083", "Al 7075", "Al 7075-T6", "Al 7175"]
    },
    {
      category: "Steel & Die Steel",
      grades: ["1018", "1045", "4130", "4140", "4340", "M-2 (HSS)", "D-2 (H.C.H.CR.)", "H-13", "En31"]
    },
    {
      category: "Stainless Steel",
      grades: ["SS303", "SS304", "SS316", "SS316L", "SS420", "17-7ph"]
    },
    {
      category: "Copper & Minor Metals",
      grades: ["C101 (T2)", "C110 (TU0)", "Brass", "Copper"]
    },
    {
      category: "Superalloys",
      grades: ["Inconel 600", "Inconel 718", "Monel 400", "Hastelloy X"]
    },
    {
      category: "Titanium",
      grades: ["Ti Grade 2", "Ti Grade 5 (Ti-6Al-4V)"]
    },
    {
      category: "Engineering Plastics",
      grades: ["PEEK", "Delrin (POM)", "Nylon 6", "UHMWPE", "PMMA"]
    }
  ];

  // Post-processing options from toolkit
  const postProcessingOptions = [
    // Electro/Chemical Class
    "Anodizing - Sulfuric Acid", "Anodizing - Hard (Type III)", "Anodizing - Porcelain",
    "Electroplating - Chrome", "Electroplating - Nickel", "Electroplating - Gold", "Electroplating - Zinc",
    "Electroless Plating - Nickel", "Electroless Plating - Copper",
    "Electropolishing", "Chemical Polishing",
    "E-Coat - Cathodic Epoxy", "E-Coat - Anodic Acrylic",
    "PVD - Sputtering", "PVD - Evaporation",
    "CVD - Thermal", "CVD - Plasma-Enhanced",
    // Coating Class
    "Spraying - Air Spray", "Spraying - Airless",
    "Powder Coating - Thermoset", "Powder Coating - Thermoplastic",
    "Painting - Enamel", "Painting - Polyurethane", "Painting - Acrylic",
    // Machining Class
    "Sandblasting", "Brushing - Hairline", "Brushing - Satin",
    "Vibratory Finishing", "Barrel Tumbling",
    "Polishing - Rough", "Polishing - Mirror"
  ];

  // Inspection options from toolkit (without machine names)
  const inspectionOptions = [
    "Optical Surface Profiling",
    "Optical 3D Surface Measurement",
    "True Color 3D Optical Profiling",
    "Surface Roughness & Contour Measurement",
    "Coordinate Measuring (CMM)",
    "Optical Coordinate Measurement",
    "Vision Measuring & 3D Scanning"
  ];

  const teamCategories = [
    "Startup",
    "Research Lab",
    "Student Team"
  ];

  const leadTimeOptions = [
    { value: "expedite", label: "Expedite (2-5 Days)", color: "text-red-400" },
    { value: "standard", label: "Standard (7-10 Days)", color: "text-amber-400" },
    { value: "economy", label: "Economy (14 Days)", color: "text-green-400" }
  ];

  const addOrderItem = () => {
    setOrderItems([
      ...orderItems,
      { id: Date.now(), partName: "", material: "", quantity: 1, stepFile: null, drawingFile: null, instructions: "", instructionsPdf: null, postProcessing: { enabled: false, process: "" }, inspection: { enabled: false, processes: [] } },
    ]);
  };

  const removeOrderItem = (id) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter((item) => item.id !== id));
    }
  };

  const updateOrderItem = (id, field, value) => {
    setOrderItems(orderItems.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  const updatePostProcessing = (id, field, value) => {
    setOrderItems(orderItems.map((item) => item.id === id ? { ...item, postProcessing: { ...item.postProcessing, [field]: value } } : item));
  };

  const updateInspection = (id, field, value) => {
    setOrderItems(orderItems.map((item) => item.id === id ? { ...item, inspection: { ...item.inspection, [field]: value } } : item));
  };

  const toggleInspectionProcess = (id, process) => {
    setOrderItems(orderItems.map((item) => {
      if (item.id !== id) return item;
      const processes = item.inspection.processes.includes(process)
        ? item.inspection.processes.filter(p => p !== process)
        : [...item.inspection.processes, process];
      return { ...item, inspection: { ...item.inspection, processes } };
    }));
  };

  // Collect all uploaded files from all order items, renaming them by part index
  const collectAllFiles = (items) => {
    const files = [];
    items.forEach((item, idx) => {
      const prefix = items.length > 1 ? `Part${idx + 1}_` : "";
      if (item.stepFile) {
        files.push(new File([item.stepFile], `${prefix}${item.stepFile.name}`, { type: item.stepFile.type }));
      }
      if (item.drawingFile) {
        files.push(new File([item.drawingFile], `${prefix}${item.drawingFile.name}`, { type: item.drawingFile.type }));
      }
      if (item.instructionsPdf) {
        files.push(new File([item.instructionsPdf], `${prefix}${item.instructionsPdf.name}`, { type: item.instructionsPdf.type }));
      }
    });
    return files;
  };

  // Handle E-NDA submission with signed PDF
  const handleENDASubmit = async (ndaPdfBlob, signatureBase64) => {
    setIsSubmitting(true);
    setShowENDAModal(false);
    
    const leadTimeLabel = leadTimeOptions.find(opt => opt.value === leadTime)?.label || "Not specified";
    
    // Generate RFQ ID: MAKE-YYYYMMDD-XXXX
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const rfqId = `MAKE-${dateStr}-${randomNum}`;
    
    // Format items array to match EmailJS template structure
    const formattedItems = orderItems.map((item) => ({
      part_name: item.partName || 'Unnamed Part',
      quantity: item.quantity,
      material: item.material || 'Not specified',
      post_processing: item.postProcessing.enabled ? item.postProcessing.process : 'None',
      inspection: item.inspection.enabled && item.inspection.processes.length > 0 
        ? item.inspection.processes.join(', ') 
        : 'None',
      step_file: item.stepFile?.name || 'Not uploaded',
      drawing_file: item.drawingFile?.name || 'Not uploaded',
      instructions: item.instructions || 'None provided'
    }));
    
    // EmailJS template data with E-NDA flag
    const orderSummary = {
      rfq_id: rfqId,
      name: userDetails.name,
      email: userDetails.ldapId,
      phone: userDetails.phone,
      team_name: userDetails.teamName,
      team_category: userDetails.teamCategory,
      lab_name: userDetails.labName || 'N/A',
      department: userDetails.department || 'N/A',
      lead_time: leadTimeLabel,
      with_enda: "Yes - Signed",
      items: formattedItems
    };

    try {
      // Using EmailJS
      const emailjs = (await import('@emailjs/browser')).default;
      
      await emailjs.send(
        'service_y1pzsdi',
        'template_kduakvj',
        orderSummary,
        'KyCO7BptOJPFmgi2N'
      );

      // Create download link for the signed NDA PDF
      const pdfUrl = URL.createObjectURL(ndaPdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfUrl;
      downloadLink.download = `NDA_Mechximize_${userDetails.name.replace(/\s+/g, '_')}_${new Date().getFullYear()}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(pdfUrl);

      // ── Upload files to Google Drive ────────────────────────────────────────────
      let driveFolderUrl = null;
      const allFiles = collectAllFiles(orderItems);

      // Include the signed NDA PDF in the Drive upload
      if (ndaPdfBlob) {
        const ndaFilename = `NDA_Mechximize_${userDetails.name.replace(/\s+/g, '_')}_${new Date().getFullYear()}.pdf`;
        allFiles.push(new File([ndaPdfBlob], ndaFilename, { type: 'application/pdf' }));
      }

      if (allFiles.length > 0) {
        try {
          const formData = new FormData();
          formData.append("rfq_id", rfqId);
          allFiles.forEach((file) => formData.append("files", file));

          const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
          const driveRes = await fetch(`${backendUrl}/api/rfq/upload-files`, {
            method: "POST",
            body: formData,
          });

          if (driveRes.ok) {
            const driveResult = await driveRes.json();
            driveFolderUrl = driveResult.folder_url;
          } else {
            console.error("Drive upload failed:", driveRes.status);
          }
        } catch (driveErr) {
          console.error("Drive upload error:", driveErr);
          // Non-fatal — RFQ was still submitted via email
        }
      }
      // ────────────────────────────────────────────────────────────────────────────

      const driveMsg = driveFolderUrl
        ? `\n\n📁 Files uploaded to Google Drive:\n${driveFolderUrl}`
        : allFiles.length > 0
        ? `\n\n⚠️ File upload to Drive failed. Please email your files to orders@mechximize.tech with subject: RFQ ${rfqId} - Attachments`
        : "";

      alert(`✅ Quote request submitted!\n\nRFQ #: ${rfqId}${driveMsg}`);
      
      // Reset form
      setStep(1);
      setOrderItems([{ id: 1, partName: "", material: "", quantity: 1, stepFile: null, drawingFile: null, instructions: "", instructionsPdf: null, postProcessing: { enabled: false, process: "" }, inspection: { enabled: false, processes: [] } }]);
      setUserDetails({ name: "", ldapId: "", teamName: "", teamCategory: "", labName: "", department: "", phone: "" });
      setLeadTime("");
      
    } catch (error) {
      console.error('EmailJS error:', error);
      
      // Still download the PDF even if email fails
      const pdfUrl = URL.createObjectURL(ndaPdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfUrl;
      downloadLink.download = `NDA_Mechximize_${userDetails.name.replace(/\s+/g, '_')}_${new Date().getFullYear()}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(pdfUrl);
      
      // Fallback to mailto
      const subject = encodeURIComponent(`RFQ ${rfqId} - Quote Request with E-NDA - ${userDetails.name}`);
      const body = encodeURIComponent(`
MAKE @ IITB - Quote Request with E-NDA
======================================
RFQ #: ${rfqId}

REQUESTER DETAILS:
- Name: ${userDetails.name}
- Email: ${userDetails.ldapId}
- Phone: ${userDetails.phone}
- Team Name: ${userDetails.teamName}
- Team Category: ${userDetails.teamCategory}
- Lab Name: ${userDetails.labName || 'N/A'}
- Department: ${userDetails.department || 'N/A'}
- Lead Time: ${leadTimeLabel}

REQUESTED PARTS:
${formattedItems.map((item, index) => `
Part ${index + 1}: ${item.part_name}
  - Material: ${item.material}
  - Quantity: ${item.quantity}
  - Post-Processing: ${item.post_processing}
  - Inspection: ${item.inspection}
  - STEP File: ${item.step_file}
  - 2D Drawing: ${item.drawing_file}
  - Instructions: ${item.instructions}`).join('')}

======================================
E-NDA SIGNED - PDF downloaded, please attach to this email
Submitted via MAKE @ IITB Portal
      `.trim());
      
      window.location.href = `mailto:orders@mechximize.tech?cc=${userDetails.ldapId}&subject=${subject}&body=${body}`;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (withEnda) => {
    setIsSubmitting(true);
    
    const leadTimeLabel = leadTimeOptions.find(opt => opt.value === leadTime)?.label || "Not specified";
    
    // Generate RFQ ID: MAKE-YYYYMMDD-XXXX
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const rfqId = `MAKE-${dateStr}-${randomNum}`;
    
    // Format items array to match EmailJS template structure
    const formattedItems = orderItems.map((item) => ({
      part_name: item.partName || 'Unnamed Part',
      quantity: item.quantity,
      material: item.material || 'Not specified',
      post_processing: item.postProcessing.enabled ? item.postProcessing.process : 'None',
      inspection: item.inspection.enabled && item.inspection.processes.length > 0 
        ? item.inspection.processes.join(', ') 
        : 'None',
      step_file: item.stepFile?.name || 'Not uploaded',
      drawing_file: item.drawingFile?.name || 'Not uploaded',
      instructions: item.instructions || 'None provided'
    }));
    
    // EmailJS template expects these exact field names
    const orderSummary = {
      rfq_id: rfqId,
      name: userDetails.name,
      email: userDetails.ldapId,
      phone: userDetails.phone,
      team_name: userDetails.teamName,
      team_category: userDetails.teamCategory,
      lab_name: userDetails.labName || 'N/A',
      department: userDetails.department || 'N/A',
      lead_time: leadTimeLabel,
      with_enda: withEnda ? "Yes" : "No",
      items: formattedItems
    };

    try {
      // Using EmailJS
      const emailjs = (await import('@emailjs/browser')).default;
      
      await emailjs.send(
        'service_y1pzsdi',
        'template_kduakvj',
        orderSummary,
        'KyCO7BptOJPFmgi2N'
      );

      // ── Upload files to Google Drive ────────────────────────────────────────────
      let driveFolderUrl = null;
      const allFiles = collectAllFiles(orderItems);

      if (allFiles.length > 0) {
        try {
          const formData = new FormData();
          formData.append("rfq_id", rfqId);
          allFiles.forEach((file) => formData.append("files", file));

          const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
          const driveRes = await fetch(`${backendUrl}/api/rfq/upload-files`, {
            method: "POST",
            body: formData,
          });

          if (driveRes.ok) {
            const driveResult = await driveRes.json();
            driveFolderUrl = driveResult.folder_url;
          } else {
            console.error("Drive upload failed:", driveRes.status);
          }
        } catch (driveErr) {
          console.error("Drive upload error:", driveErr);
          // Non-fatal — RFQ was still submitted via email
        }
      }
      // ────────────────────────────────────────────────────────────────────────────

      const driveMsg = driveFolderUrl
        ? `\n\n📁 Files uploaded to Google Drive:\n${driveFolderUrl}`
        : allFiles.length > 0
        ? `\n\n⚠️ File upload to Drive failed. Please email your files to orders@mechximize.tech with subject: RFQ ${rfqId} - Attachments`
        : "";

      alert(`✅ Quote request submitted!\n\nRFQ #: ${rfqId}${driveMsg}`);
      
      // Reset form
      setStep(1);
      setOrderItems([{ id: 1, partName: "", material: "", quantity: 1, stepFile: null, drawingFile: null, instructions: "", instructionsPdf: null, postProcessing: { enabled: false, process: "" }, inspection: { enabled: false, processes: [] } }]);
      setUserDetails({ name: "", ldapId: "", teamName: "", teamCategory: "", labName: "", department: "", phone: "" });
      setLeadTime("");
      
    } catch (error) {
      console.error('EmailJS error:', error);
      // Fallback to mailto with RFQ ID
      const subject = encodeURIComponent(`RFQ ${rfqId} - Quote Request${withEnda ? ' with E-NDA' : ''} - ${userDetails.name}`);
      const body = encodeURIComponent(`
MAKE @ IITB - Quote Request${withEnda ? ' with E-NDA' : ''}
======================================
RFQ #: ${rfqId}

REQUESTER DETAILS:
- Name: ${userDetails.name}
- Email: ${userDetails.ldapId}
- Phone: ${userDetails.phone}
- Team Name: ${userDetails.teamName}
- Team Category: ${userDetails.teamCategory}
- Lab Name: ${userDetails.labName || 'N/A'}
- Department: ${userDetails.department || 'N/A'}
- Lead Time: ${leadTimeLabel}

REQUESTED PARTS:
${formattedItems.map((item, index) => `
Part ${index + 1}: ${item.part_name}
  - Material: ${item.material}
  - Quantity: ${item.quantity}
  - Post-Processing: ${item.post_processing}
  - Inspection: ${item.inspection}
  - STEP File: ${item.step_file}
  - 2D Drawing: ${item.drawing_file}
  - Instructions: ${item.instructions}`).join('')}

======================================
${withEnda ? 'Request includes E-NDA requirement' : 'Standard quote request'}
Submitted via MAKE @ IITB Portal
      `.trim());
      
      window.location.href = `mailto:orders@mechximize.tech?cc=${userDetails.ldapId}&subject=${subject}&body=${body}`;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-20 min-h-screen bg-[#0A0A0A] noise-overlay">
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-10">
          <p className="font-accent text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">Start Building</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">BUILD NOW</h1>
          
          {/* Step Indicator - Clickable */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => setStep(1)}
              className={`flex items-center gap-2 ${step >= 1 ? 'text-amber-500' : 'text-gray-600'} transition-colors`}
              data-testid="step-1-btn"
            >
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-mono text-sm ${step >= 1 ? 'border-amber-500 bg-amber-500/10' : 'border-gray-600'}`}>1</div>
              <span className="font-mono text-xs uppercase hidden sm:inline">Your Parts</span>
            </button>
            <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-amber-500' : 'bg-gray-700'}`}></div>
            <button
              onClick={() => step === 2 && setStep(2)}
              className={`flex items-center gap-2 ${step >= 2 ? 'text-amber-500' : 'text-gray-600'} transition-colors ${step < 2 ? 'cursor-not-allowed' : ''}`}
              disabled={step < 2}
            >
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-mono text-sm ${step >= 2 ? 'border-amber-500 bg-amber-500/10' : 'border-gray-600'}`}>2</div>
              <span className="font-mono text-xs uppercase hidden sm:inline">Your Details</span>
            </button>
          </div>
        </motion.div>

        {/* Step 1: Parts */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-6">
            <h3 className="font-mono text-sm uppercase tracking-wider text-gray-400 border-b border-[#262626] pb-2">Your Parts</h3>
            
            {orderItems.map((item, index) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="order-row border border-[#262626] bg-[#050505] p-5" data-testid={`order-item-${index}`}>
                <div className="flex justify-between items-center mb-4">
                  <span className="font-mono text-xs text-gray-500">PART #{index + 1}</span>
                  {orderItems.length > 1 && (
                    <button onClick={() => removeOrderItem(item.id)} className="text-gray-500 hover:text-red-500 transition-colors" data-testid={`remove-item-${index}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Part Name</label>
                    <input type="text" value={item.partName} onChange={(e) => updateOrderItem(item.id, "partName", e.target.value)} className="form-input w-full text-white" placeholder="Enter part name" data-testid={`part-name-${index}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Material</label>
                    <Select value={item.material} onValueChange={(value) => updateOrderItem(item.id, "material", value)}>
                      <SelectTrigger className="w-full bg-transparent border-0 border-b border-[#262626] rounded-none focus:ring-0 font-mono text-white" data-testid={`material-select-${index}`}>
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-[#262626] max-h-[300px]">
                        {materialCategories.map((cat) => (
                          <div key={cat.category}>
                            <div className="px-3 py-2 text-xs font-mono text-amber-500 uppercase tracking-wider bg-[#111] sticky top-0">{cat.category}</div>
                            {cat.grades.map((grade) => (
                              <SelectItem key={`${cat.category}-${grade}`} value={`${cat.category} - ${grade}`} className="font-mono text-sm pl-6">{grade}</SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Quantity</label>
                    <input type="number" min="1" value={item.quantity} onChange={(e) => updateOrderItem(item.id, "quantity", parseInt(e.target.value) || 1)} className="form-input w-full text-white" data-testid={`quantity-${index}`} />
                  </div>
                </div>

                {/* File Uploads */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">STEP File (.stp)</label>
                    <label className="upload-zone flex flex-col items-center justify-center p-4 cursor-pointer">
                      <Upload className="w-5 h-5 text-gray-500 mb-1" />
                      <span className="text-xs text-gray-500 font-mono">{item.stepFile?.name || "Click to upload"}</span>
                      <input type="file" accept=".stp,.step" className="hidden" onChange={(e) => updateOrderItem(item.id, "stepFile", e.target.files[0])} data-testid={`step-file-${index}`} />
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">2D Drawing (.pdf)</label>
                    <label className="upload-zone flex flex-col items-center justify-center p-4 cursor-pointer">
                      <FileText className="w-5 h-5 text-gray-500 mb-1" />
                      <span className="text-xs text-gray-500 font-mono">{item.drawingFile?.name || "Click to upload"}</span>
                      <input type="file" accept=".pdf" className="hidden" onChange={(e) => updateOrderItem(item.id, "drawingFile", e.target.files[0])} data-testid={`drawing-file-${index}`} />
                    </label>
                  </div>
                </div>

                {/* Instructions Section */}
                <div className="mt-5 p-4 border border-[#262626] bg-[#0A0A0A]">
                  <label className="flex items-center gap-2 text-xs font-mono text-gray-400 mb-3 uppercase">
                    <MessageSquare className="w-4 h-4" />
                    Instructions (Optional)
                  </label>
                  <textarea
                    value={item.instructions}
                    onChange={(e) => updateOrderItem(item.id, "instructions", e.target.value)}
                    className="w-full bg-transparent border border-[#262626] p-3 text-white text-sm font-mono placeholder:text-gray-600 focus:outline-none focus:border-gray-500 min-h-[80px]"
                    placeholder="Type any special instructions here..."
                    data-testid={`instructions-${index}`}
                  />
                  <div className="mt-3 flex items-center gap-4">
                    <span className="text-xs text-gray-500">OR</span>
                    <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-[#262626] text-gray-500 hover:text-white hover:border-gray-500 cursor-pointer transition-colors text-xs font-mono">
                      <Upload className="w-3 h-3" />
                      {item.instructionsPdf?.name || "Upload PDF"}
                      <input type="file" accept=".pdf" className="hidden" onChange={(e) => updateOrderItem(item.id, "instructionsPdf", e.target.files[0])} data-testid={`instructions-pdf-${index}`} />
                    </label>
                  </div>
                </div>

                {/* Post-Processing Option - Simplified */}
                <div className="mt-4 p-4 border border-[#262626] bg-[#0A0A0A]">
                  <label className="flex items-center gap-3 cursor-pointer mb-3">
                    <Checkbox checked={item.postProcessing.enabled} onCheckedChange={(checked) => updatePostProcessing(item.id, "enabled", checked)} className="border-[#262626] data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500" data-testid={`post-processing-checkbox-${index}`} />
                    <span className="font-mono text-sm text-gray-300">Add Post-Processing</span>
                  </label>
                  
                  {item.postProcessing.enabled && (
                    <div className="pl-6">
                      <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Process</label>
                      <Select value={item.postProcessing.process} onValueChange={(value) => updatePostProcessing(item.id, "process", value)}>
                        <SelectTrigger className="w-full md:w-1/2 bg-transparent border border-[#262626] rounded-none focus:ring-0 font-mono text-white text-xs">
                          <SelectValue placeholder="Select process" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                          {postProcessingOptions.map((opt) => (<SelectItem key={opt} value={opt} className="font-mono text-xs">{opt}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Inspection Option - Simplified */}
                <div className="mt-4 p-4 border border-[#262626] bg-[#0A0A0A]">
                  <label className="flex items-center gap-3 cursor-pointer mb-3">
                    <Checkbox checked={item.inspection.enabled} onCheckedChange={(checked) => updateInspection(item.id, "enabled", checked)} className="border-[#262626] data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" data-testid={`inspection-checkbox-${index}`} />
                    <span className="font-mono text-sm text-gray-300">Add Inspection</span>
                  </label>
                  
                  {item.inspection.enabled && (
                    <div className="pl-6">
                      <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Select Inspection Types (Multiple)</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {inspectionOptions.map((opt) => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer p-2 border border-[#262626] hover:border-green-500/50">
                            <Checkbox checked={item.inspection.processes.includes(opt)} onCheckedChange={() => toggleInspectionProcess(item.id, opt)} className="border-[#262626] data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                            <span className="text-xs text-gray-400">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}

            <button onClick={addOrderItem} className="w-full border border-dashed border-[#262626] py-4 text-gray-500 hover:text-white hover:border-white transition-colors font-mono text-sm uppercase tracking-wider flex items-center justify-center gap-2" data-testid="add-part-button">
              <Plus className="w-4 h-4" />Add Another Part
            </button>

            <div className="flex justify-end pt-6">
              <Button onClick={() => setStep(2)} className="btn-mechanical bg-amber-500 text-black hover:bg-amber-400 font-mono uppercase tracking-wider text-sm rounded-none px-8 py-4" data-testid="checkout-button">
                Checkout <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: User Details */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            {/* Back Arrow */}
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4" data-testid="back-arrow">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-mono text-sm">Back to Parts</span>
            </button>

            <h3 className="font-mono text-sm uppercase tracking-wider text-gray-400 border-b border-[#262626] pb-2">Your Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Name *</label>
                <input type="text" value={userDetails.name} onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })} className="form-input w-full text-white" placeholder="Your full name" data-testid="user-name" />
              </div>
              <div>
                <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">LDAP ID (Email) *</label>
                <input type="email" value={userDetails.ldapId} onChange={(e) => setUserDetails({ ...userDetails, ldapId: e.target.value })} className="form-input w-full text-white" placeholder="yourname@iitb.ac.in" data-testid="ldap-id" />
              </div>
              <div>
                <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Team Name</label>
                <input type="text" value={userDetails.teamName} onChange={(e) => setUserDetails({ ...userDetails, teamName: e.target.value })} className="form-input w-full text-white" placeholder="Your team/startup name" data-testid="team-name" />
              </div>
              <div>
                <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Lab Name</label>
                <input type="text" value={userDetails.labName} onChange={(e) => setUserDetails({ ...userDetails, labName: e.target.value })} className="form-input w-full text-white" placeholder="Associated lab (if any)" data-testid="lab-name" />
              </div>
              <div>
                <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Department</label>
                <input type="text" value={userDetails.department} onChange={(e) => setUserDetails({ ...userDetails, department: e.target.value })} className="form-input w-full text-white" placeholder="Department/Institute" data-testid="department" />
              </div>
              <div>
                <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Phone Number</label>
                <input type="tel" value={userDetails.phone} onChange={(e) => setUserDetails({ ...userDetails, phone: e.target.value })} className="form-input w-full text-white" placeholder="+91 XXXXX XXXXX" data-testid="phone" />
              </div>
              <div>
                <label className="block text-xs font-mono text-gray-500 mb-2 uppercase">Team Category *</label>
                <Select value={userDetails.teamCategory} onValueChange={(value) => setUserDetails({ ...userDetails, teamCategory: value })}>
                  <SelectTrigger className="w-full bg-transparent border-0 border-b border-[#262626] rounded-none focus:ring-0 font-mono text-white" data-testid="team-category-select">
                    <SelectValue placeholder="Select team category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                    {teamCategories.map((cat) => (<SelectItem key={cat} value={cat} className="font-mono text-sm">{cat}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Order Summary */}
            <div className="border border-[#262626] bg-[#050505] p-5">
              <h4 className="font-mono text-xs uppercase tracking-wider text-gray-400 mb-4">Order Summary</h4>
              <div className="space-y-2">
                {orderItems.map((item, index) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-400">{item.partName || `Part ${index + 1}`} ({item.material || 'No material'}) × {item.quantity}</span>
                    <div className="flex gap-2">
                      {item.postProcessing.enabled && <span className="text-purple-400 text-xs px-2 py-0.5 bg-purple-400/10">PP</span>}
                      {item.inspection.enabled && item.inspection.processes.length > 0 && <span className="text-green-400 text-xs px-2 py-0.5 bg-green-400/10">QC</span>}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Lead Time Selection */}
              <div className="mt-6 pt-4 border-t border-[#262626]">
                <label className="block text-xs font-mono text-gray-500 mb-3 uppercase">Lead Time *</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {leadTimeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setLeadTime(option.value)}
                      className={`p-3 border text-left transition-all ${
                        leadTime === option.value 
                          ? 'border-amber-500 bg-amber-500/10' 
                          : 'border-[#262626] hover:border-gray-600'
                      }`}
                      data-testid={`lead-time-${option.value}`}
                    >
                      <span className={`font-mono text-sm ${leadTime === option.value ? 'text-amber-500' : option.color}`}>
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Two Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => handleSubmit(false)}
                disabled={!userDetails.name || !userDetails.ldapId || !userDetails.teamCategory || !leadTime || isSubmitting}
                className="flex-1 btn-mechanical bg-transparent border border-white text-white hover:bg-white hover:text-black font-mono uppercase tracking-wider text-sm rounded-none py-5 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="get-quote-button"
              >
                {isSubmitting ? "SENDING..." : "GET QUOTE"}
              </Button>
              <Button
                onClick={() => setShowENDAModal(true)}
                disabled={!userDetails.name || !userDetails.ldapId || !userDetails.teamCategory || !leadTime || isSubmitting}
                className="flex-1 btn-mechanical pulse-glow bg-amber-500 text-black hover:bg-amber-400 font-mono uppercase tracking-wider text-sm rounded-none py-5 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="get-quote-enda-button"
              >
                {isSubmitting ? "SENDING..." : "REVIEW & SIGN NDA"}
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* E-NDA Modal */}
      <ENDAModal
        isOpen={showENDAModal}
        onClose={() => setShowENDAModal(false)}
        formData={userDetails}
        orderItems={orderItems}
        leadTime={leadTime}
        onSignAndSubmit={handleENDASubmit}
      />
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-[#050505] border-t border-[#171717] py-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <h3 className="font-heading font-bold text-lg text-white mb-1">MAKE<span className="text-amber-500">@</span>IITB</h3>
            <p className="text-gray-500 text-sm">A Mechximize x BETIC Initiative</p>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <a href="mailto:orders@mechximize.tech" className="link-terminal text-gray-400 hover:text-white text-sm font-mono">orders@mechximize.tech</a>
            <span className="text-gray-600 text-sm">IIT Bombay, Powai, Mumbai</span>
          </div>
        </div>
        
        {/* Partner Logos */}
        <div className="mt-8 pt-6 border-t border-[#171717] flex justify-center items-center gap-8">
          <a href="https://www.mechximize.in" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity">
            <img 
              src="https://customer-assets.emergentagent.com/job_handshake-make/artifacts/a7urfhq7_image-removebg-preview%20%287%29.png" 
              alt="Mechximize" 
              className="h-10 object-contain"
            />
          </a>
          <a href="https://www.betic.org" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity">
            <img 
              src="https://customer-assets.emergentagent.com/job_handshake-make/artifacts/re7obkqw_image-removebg-preview%20%286%29.png" 
              alt="BETIC" 
              className="h-10 object-contain"
            />
          </a>
        </div>
        
        <div className="mt-6 pt-6 border-t border-[#171717] text-center">
          <p className="text-gray-600 text-xs font-mono">© {new Date().getFullYear()} MAKE @ IITB. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [activeTab, setActiveTab] = useState("home");

  const renderTabContent = () => {
    switch (activeTab) {
      case "home":
        return <HomeTab setActiveTab={setActiveTab} />;
      case "initiative":
        return <InitiativePage />;
      case "mechximize-about":
        return <MechximizeAboutPage />;
      case "toolkit":
        return <ToolkitTab />;
      case "build-now":
        return <BuildNowTab />;
      default:
        return <HomeTab setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="App bg-[#050505] min-h-screen">
      <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
      {renderTabContent()}
      <Footer />
    </div>
  );
}

export default App;
